/**
 * Noten je Persona in der App (Lauf-Detail). Dieselbe Darstellung und
 * dieselbe Reihenfolge wie „Noten je Persona" im Bericht: beide Noten, der
 * Abstand als Balken von der Mitte aus, sortiert nach Abstand
 * (`personaNoten`). Nur die Farben kommen aus dem Look der App statt aus dem
 * Papier des Berichts.
 *
 * Benutzt von der Lauf-Detailseite und vom Bericht (`seiten/Bericht.tsx`,
 * seit 26.09.2026). Der Bericht wird gedruckt: Zeilen brechen deshalb nicht
 * ueber eine Seite.
 */

import { Fragment } from 'react';
import { personaNoten, type PersonaNote } from './bericht-noten';
import { locale, sprache, t } from './sprache';

const zahl = (n: number) =>
  new Intl.NumberFormat(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);

const personen = (n: number) => (sprache() === 'en' ? (n === 1 ? 'one persona' : `${n} personas`) : n === 1 ? 'eine Persona' : `${n} Personas`);

const istRand = (p: PersonaNote) => p.rolle === 'rand';

/** Urteilt mindestens eine Persona mehrmals (Persona × Job)? Dann sind Personas und Urteile verschiedene Zahlen. */
export const mehrfach = (noten: PersonaNote[]) => noten.some(p => (p.urteile ?? 1) > 1);

/**
 * Der Satz über der Tabelle, wie im Bericht. Exportiert für den Test. Hat der
 * Lauf Kern und Rand, zählt der Satz nur den Kern, denn das Urteil folgt ihm.
 * Urteilen Personas mehrmals, zählt der erste Teil Personas (nach dem Mittel
 * ihrer Noten) und ein zweiter Satz die Urteile (seit 09.10.2026: vorher
 * stand „34 Personas“, wo 34 Urteile von 13 Personas gemeint waren).
 */
export function notenSatz(noten: PersonaNote[]): string {
  const kern = noten.filter(p => !istRand(p));
  const getrennt = kern.length > 0 && kern.length < noten.length;
  const basis = getrennt ? kern : noten;
  const hoeher = basis.filter(p => p.delta > 0).length;
  const tiefer = basis.filter(p => p.delta < 0).length;
  const gleich = basis.length - hoeher - tiefer;
  if (sprache() === 'en') return notenSatzEn(basis, getrennt, hoeher, tiefer, gleich);
  const mittel = mehrfach(basis) ? ' im Mittel' : '';
  const verb = (n: number) => (n === 1 ? 'bewertet' : 'bewerten');
  // Das Verb steht beim ersten Teil; die folgenden teilen es sich.
  const teile: string[] = [];
  if (hoeher) teile.push(`${personen(hoeher)} ${verb(hoeher)} die geprüfte Fassung${mittel} höher`);
  if (tiefer) teile.push(teile.length ? `${personen(tiefer)} die Vergleichsfassung` : `${personen(tiefer)} ${verb(tiefer)} die Vergleichsfassung${mittel} höher`);
  if (gleich) teile.push(teile.length ? `${personen(gleich)} beide gleich` : `${personen(gleich)} ${verb(gleich)} beide gleich`);
  const satz = teile.join(', ');
  if (!satz) return '';
  const erster = getrennt ? `Im Kern: ${satz}.` : `${satz.charAt(0).toUpperCase()}${satz.slice(1)}.`;
  return mittel ? `${erster} ${urteileSatz(basis)}`.trim() : erster;
}

/** Der Satz auf Englisch, gleich gebaut. */
function notenSatzEn(basis: PersonaNote[], getrennt: boolean, hoeher: number, tiefer: number, gleich: number): string {
  const mittel = mehrfach(basis) ? ' on average' : '';
  const verb = (n: number) => (n === 1 ? 'rates' : 'rate');
  const teile: string[] = [];
  if (hoeher) teile.push(`${personen(hoeher)} ${verb(hoeher)} the tested version higher${mittel}`);
  if (tiefer) teile.push(teile.length ? `${personen(tiefer)} the baseline` : `${personen(tiefer)} ${verb(tiefer)} the baseline higher${mittel}`);
  if (gleich) teile.push(teile.length ? `${personen(gleich)} both the same` : `${personen(gleich)} ${verb(gleich)} both the same`);
  const satz = teile.join(', ');
  if (!satz) return '';
  const erster = getrennt ? `In the core audience: ${satz}.` : `${satz.charAt(0).toUpperCase()}${satz.slice(1)}.`;
  return mittel ? `${erster} ${urteileSatz(basis)}`.trim() : erster;
}

/** „Von ihren 39 Urteilen wählen 34 die geprüfte Fassung.“ Leer, wenn kein Urteil eine Wahl trägt. */
export function urteileSatz(noten: PersonaNote[]): string {
  const urteile = noten.reduce((s, p) => s + (p.urteile ?? 1), 0);
  const fuer = noten.reduce((s, p) => s + (p.fuerGeprueft ?? (p.urteile ? 0 : Number(p.gewinner === 'geprueft'))), 0);
  const gegen = noten.reduce((s, p) => s + (p.fuerVergleich ?? (p.urteile ? 0 : Number(p.gewinner === 'vergleich'))), 0);
  if (!fuer && !gegen) return '';
  return t(
    `Von ihren ${urteile} Urteilen ${fuer === 1 ? 'wählt eines' : `wählen ${fuer}`} die geprüfte Fassung.`,
    `Of their ${urteile} verdicts, ${fuer === 1 ? 'one chooses' : `${fuer} choose`} the tested version.`,
  );
}

export function NotenJePersona({ quelle, ohneSatz = false }: {
  quelle: unknown;
  /** Der Satz steht schon daneben (Lauf-Detail: in der Zeile zum Aufklappen). */
  ohneSatz?: boolean;
}) {
  const noten = personaNoten(quelle);
  if (noten.length === 0) return null;
  const skala = Math.max(1, ...noten.map(p => Math.abs(p.delta)));
  return (
    <>
      {!ohneSatz && <p className="mt-0 mb-3 text-[0.9375rem] leading-relaxed text-text">{notenSatz(noten)}</p>}
      <div className="overflow-hidden rounded-karte print:shadow-none border border-linie bg-flaeche text-[0.9375rem] shadow-flaeche">
        <div className="hidden grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_11rem] gap-4 bg-ebene px-5 py-2.5 text-[0.6875rem] font-semibold tracking-[0.06em] text-text-hell uppercase sm:grid">
          <span>{t('Persona', 'Persona')}</span><span className="text-right">{t('Vergleich', 'Baseline')}</span><span className="text-right">{t('Geprüft', 'Tested')}</span><span className="text-right">{t('Abstand', 'Gap')}</span>
        </div>
        {noten.map((p, i) => (
          <Fragment key={`${p.persona}-${i}`}>
            {/* Randpersonas stehen nach dem Kern (personaNoten), mit eigener Kopfzeile. */}
            {istRand(p) && i > 0 && !istRand(noten[i - 1]!) && (
              <div className="border-t border-linie bg-ebene px-4 py-2 text-[0.75rem] font-semibold text-text-leise sm:px-5">
                {t('Erweiterte Zielgruppe, zählt nicht ins Urteil', 'Extended audience, not counted in the verdict')}
              </div>
            )}
            <NotenZeile p={p} skala={skala} />
          </Fragment>
        ))}
      </div>
      <p className="mt-2 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">
        {t('Noten bis 10, sortiert nach Abstand. „Wählt“ nennt die Fassung, die die Persona im direkten Vergleich vorzieht.',
          'Scores out of 10, sorted by gap. “Chooses” names the version the persona prefers in a direct comparison.')}
        {mehrfach(noten) && t(' Wer den Text in mehreren Situationen gelesen hat, steht einmal da, mit dem Mittel seiner Noten und der Zahl seiner Urteile.',
          ' Anyone who read the copy in several situations appears once, with the average of their scores and the number of their verdicts.')}
      </p>
    </>
  );
}

function NotenZeile({ p, skala }: { p: PersonaNote; skala: number }) {
  const breite = `${(Math.abs(p.delta) / skala) * 50}%`;
  const plus = p.delta > 0;
  const wahl = p.urteile
    ? t(`${p.fuerGeprueft ?? 0} von ${p.urteile} Urteilen für die geprüfte Fassung`, `${p.fuerGeprueft ?? 0} of ${p.urteile} ${p.urteile === 1 ? 'verdict' : 'verdicts'} for the tested version`)
    : p.gewinner === 'geprueft' ? t('wählt die geprüfte Fassung', 'chooses the tested version') : p.gewinner === 'vergleich' ? t('wählt die Vergleichsfassung', 'chooses the baseline') : null;
  return (
    <div className="grid break-inside-avoid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 border-t border-linie-zart px-4 py-3 max-sm:[&:nth-child(2)]:border-t-0 sm:grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_11rem] sm:px-5">
      <div className="col-span-2 min-w-0 sm:col-span-1">
        <div className="font-medium text-tinte [overflow-wrap:anywhere]">
          {p.persona}
          {istRand(p) && <RandEtikett />}
        </div>
        {wahl && <div className="text-[0.8125rem] text-text-hell">{wahl}</div>}
      </div>
      <span className="text-[0.875rem] text-text-leise tabular-nums sm:text-right">
        <span className="sm:hidden">{zahl(p.vergleich)} → <span className="font-semibold text-tinte">{zahl(p.geprueft)}</span></span>
        <span className="hidden sm:inline">{zahl(p.vergleich)}</span>
      </span>
      <span className="hidden text-right font-semibold text-tinte tabular-nums sm:inline">{zahl(p.geprueft)}</span>
      <span className="flex items-center justify-end gap-2">
        <span className="relative h-2 w-24 rounded-full bg-linie-zart" aria-hidden>
          <span className="absolute inset-y-[-3px] left-1/2 w-px bg-text-hell/60" />
          <span
            className={`absolute inset-y-0 rounded-full ${plus ? 'bg-marke' : 'bg-text-hell/55'}`}
            style={plus ? { left: '50%', width: breite } : { right: '50%', width: breite }}
          />
        </span>
        <span className={`w-10 text-right tabular-nums ${plus ? 'font-semibold text-marke-tief' : 'text-text-leise'}`}>
          {p.delta > 0 ? '+' : p.delta < 0 ? '−' : '±'}{zahl(Math.abs(p.delta))}
        </span>
      </span>
    </div>
  );
}

/** Das Etikett an einer Randpersona. Leise: es ordnet ein, es wertet nicht. */
export function RandEtikett() {
  return (
    <span className="ml-2 inline-block rounded-md border border-linie bg-ebene px-1.5 py-px align-[0.08em] text-[0.6875rem] font-semibold whitespace-nowrap text-text-leise">
      {t('erweiterte Zielgruppe', 'extended audience')}
    </span>
  );
}
